import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { CarHireFilterCategory } from 'constants/carHire'

function useCarHireLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const pickupDropoffLocationSearchTitleLabel = intl.formatMessage({
      id: '7EUTA58gFUXuWFYYG0jR',
      defaultMessage: 'Pick-up & Drop-off location',
      description: 'Title label for combined pick-up and drop-off location search in car hire',
    })

    const dropoffLocationSearchTitleLabel = intl.formatMessage({
      id: 'n8MaItGMzLRU368xlqsN',
      defaultMessage: 'Drop-off location',
      description: 'Title label for drop-off location search in car hire',
    })

    const pickupLocationSearchTitleLabel = intl.formatMessage({
      id: 'RvoQxP13MCE6RdvBI6SM',
      defaultMessage: 'Pick-up location',
      description: 'Title label for pick-up location search in car hire',
    })

    const pickupLocationSearchPlaceholderLabel = intl.formatMessage({
      id: 'uf0B6LiPQ4xedD7eaho4',
      defaultMessage: 'Search for an airport or city',
      description: 'Placeholder text for car hire location search input',
    })

    const carHireLuggageCategories: Array<CarHireFilterCategory> = [{
      id: '1',
      label: intl.formatMessage({
        id: 'RP6o5luIc5tGdIWdvvTZ',
        defaultMessage: '0 - 2 bags',
        description: 'Car hire luggage filter category for 0 to 2 bags in the car hire section',
      }),
      min: 0,
      max: 2,
    }, {
      id: '2',
      label: intl.formatMessage({
        id: '2P50yPnxR4vaFQHOUDiX',
        defaultMessage: '3 - 4 bags',
        description: 'Car hire luggage filter category for 3 to 4 bags in the car hire section',
      }),
      min: 3,
      max: 4,
    }, {
      id: '3',
      label: intl.formatMessage({
        id: '7NzVcYiLASKU2w41tStg',
        defaultMessage: '5 + bags',
        description: 'Car hire luggage filter category for 5 or more bags',
      }),
      min: 5,
      max: Number.MAX_SAFE_INTEGER,
    }]

    const carHireSeatCategories: Array<CarHireFilterCategory> = [{
      id: '1',
      label: intl.formatMessage({
        id: '-JIf07l682HHY64bERdj',
        defaultMessage: '1 - 4',
        description: 'Car hire seat filter category for 1 to 4 seats in the car hire section',
      }),
      min: 1,
      max: 4,
    }, {
      id: '2',
      label: intl.formatMessage({
        id: 'v5Hg6hjOJWbpNDeqJXz6',
        defaultMessage: '5 - 6',
        description: 'Car hire seat filter category for 5 to 6 seats in the car hire section',
      }),
      min: 5,
      max: 6,
    }, {
      id: '3',
      label: intl.formatMessage({
        id: 'kJDdFbtbeKEFssFjsK1w',
        defaultMessage: '7 +',
        description: 'Car hire seat filter category for 7 or more seats in the car hire section',
      }),
      min: 7,
      max: Number.MAX_SAFE_INTEGER,
    }]

    const driverCategories: Array<{ id: App.CarHireDriverAgeCategory, label: string }> = [
      {
        id: 1,
        label: intl.formatMessage({
          id: 'zttcsP1SFxDoJLHl0Uyz',
          defaultMessage: '18 - 29',
          description: 'Car hire driver age category for ages 18 to 29 in the car hire section',
        }),
      },
      {
        id: 2,
        label: intl.formatMessage({
          id: 'lIejht6U-FdpH6WdQBY8',
          defaultMessage: '30 - 69',
          description: 'Car hire driver age category for ages 30 to 69 in the car hire section',
        }),
      },
      {
        id: 3,
        label: intl.formatMessage({
          id: '4CNnP7sqlqYMQFLjA7xG',
          defaultMessage: '70+',
          description: 'Car hire driver age category for ages 70 and above',
        }),
      },
    ]

    const carHireFilterKeyToLabel = new Map<string, string>([
      ['luggage', intl.formatMessage({
        id: '41RBCvXGaQnlLl6EjxXf',
        defaultMessage: 'Luggage capacity',
        description: 'Car hire filter label for luggage capacity in the filters panel',
      })],
      ['supplier', intl.formatMessage({
        id: 'WFdSOn7y1zmI6u3zLJJL',
        defaultMessage: 'Supplier',
        description: 'Car hire filter label for supplier in the search or listing filters panel',
      })],
      ['fuelType', intl.formatMessage({
        id: '96V71GOuI3fICyox6kS3',
        defaultMessage: 'Fuel type',
        description: 'Car hire filter label for fuel type in the search or listing filters panel',
      })],
      ['seats', intl.formatMessage({
        id: 'BHp8IDdLeb9sM-XfFn4S',
        defaultMessage: 'Number of seats',
        description: 'Car hire filter label for number of seats in the filters panel',
      })],
      ['transmission', intl.formatMessage({
        id: 'DbYC-tuNua_LLum5nA6e',
        defaultMessage: 'Transmission',
        description: 'Car hire filter label for transmission type in the filters panel',
      })],
      ['pickUpType', intl.formatMessage({
        id: '_NPXXv_GOeGKuUCJWY3G',
        defaultMessage: 'Pick-up location',
        description: 'Car hire filter label for pick-up location type in the car hire section',
      })],
      ['mileage', intl.formatMessage({
        id: 'oSJbHlPKmpENZ1P1FdXx',
        defaultMessage: 'Mileage',
        description: 'Car hire filter label for mileage option in the filters panel',
      })],
      ['carTypes', intl.formatMessage({
        id: 'KAfi_0F75uBLxGnimR-C',
        defaultMessage: 'Car types',
        description: 'Car hire filter label for car types in the search or listing filters panel',
      })],
      ['payFullyNow', intl.formatMessage({
        id: 'cBGQGDFZ0-ctQglvJwnH',
        defaultMessage: 'Pay in full',
        description: 'Car hire payment schedule label for paying in full now',
      })],
      ['payPartiallyNow', intl.formatMessage({
        id: 'gPG5O4mIXgQsaXHdprD_',
        defaultMessage: 'Pay deposit',
        description: 'Car hire payment schedule label for paying a deposit',
      })],
      ['payFullyOnArrival', intl.formatMessage({
        id: 'j_fwBo9t8hllx_4bbfsL',
        defaultMessage: 'Pay at pickup',
        description: 'Car hire payment schedule label for paying at pickup',
      })],
    ])

    const pickupTypeLabelMap = new Map<App.CarHirePickupType, string>([
      ['office', intl.formatMessage({
        id: 'NpQPKZIc1p3eRZ9vGYFv',
        defaultMessage: 'Pick-up at rental office',
        description: 'Car hire pick-up type label for rental office location',
      })],
      ['shuttle', intl.formatMessage({
        id: 'Wl8-wb-JA0NJkHfNiiDg',
        defaultMessage: 'Pick-up by free shuttle',
        description: 'Car hire pick-up type label for free shuttle service',
      })],
      ['terminal', intl.formatMessage({
        id: 'dC1JBC72ap4HYdFz9Bvs',
        defaultMessage: 'Pick-up in terminal',
        description: 'Car hire pick-up type label for in-terminal pickup',
      })],
    ])

    const carHireFuelTypeLabelMap = new Map<App.CarHireVehicleFuelType, string>([
      ['Diesel', intl.formatMessage({
        id: '_lvsjD1PzFJAoBJNLKT6',
        defaultMessage: 'Diesel',
        description: 'Car hire fuel type label for diesel vehicles in car hire section',
      })],
      ['Electric', intl.formatMessage({
        id: 'xZjZBK4oUv3eLHeIGMBo',
        defaultMessage: 'Electric',
        description: 'Car hire fuel type label for electric vehicles in the car hire section',
      })],
      ['Hybrid', intl.formatMessage({
        id: 'PwZXziaK-WDiTn3p6Ph-',
        defaultMessage: 'Hybrid',
        description: 'Car hire fuel type label for hybrid vehicles in car hire section',
      })],
      ['Petrol', intl.formatMessage({
        id: 'Z6WLVC2oC9LXj7aTfPF_',
        defaultMessage: 'Petrol',
        description: 'Car hire fuel type label for petrol vehicles in car hire section',
      })],
      ['MultiFuel', intl.formatMessage({
        id: '1aOSYq7OZwPD9FVdx7v3',
        defaultMessage: 'Multi-fuel',
        description: 'Car hire fuel type label for multi-fuel vehicles in the car hire section',
      })],
      ['Unspecified', intl.formatMessage({
        id: 'dX2sSXXBW7Xs16oasGt5',
        defaultMessage: 'Unspecified',
        description: 'Car hire fuel type label for unspecified fuel type',
      })],
      ['PlugInHybrid', intl.formatMessage({
        id: '0VXJ_Mpbvn0dgM0csOBI',
        defaultMessage: 'Plug-in hybrid',
        description: 'Car hire fuel type label for plug-in hybrid vehicles',
      })],
    ])

    return {
      pickupDropoffLocationSearchTitleLabel,
      dropoffLocationSearchTitleLabel,
      pickupLocationSearchTitleLabel,
      pickupLocationSearchPlaceholderLabel,
      carHireLuggageCategories,
      carHireSeatCategories,
      driverCategories,
      carHireFilterKeyToLabel,
      pickupTypeLabelMap,
      carHireFuelTypeLabelMap,
    }
  }, [intl])
}

export default useCarHireLabels
